import React, { useState, useEffect } from "react";
import { Marker } from "react-leaflet";
import ReactDOM from "react-dom";
import L from "leaflet";
import { FaCube } from "react-icons/fa";
import ReactDOMServer from "react-dom/server";
import Ply3DTooltip from "./Ply3DTooltip";
import { getClientType } from "../../../services/clientConfig";
import { fetchClientJSON } from "../../../services/clientDataService";
import "../styles/ply3DMarkers.css";

// Create custom 3D icon
const create3DIcon = () => {
  const iconHtml = ReactDOMServer.renderToString(
    <div
      style={{
        width: "32px",
        height: "32px",
        backgroundColor: "#A1E340",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid white",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
      }}
    >
      <FaCube style={{ color: "white", fontSize: "18px" }} />
    </div>
  );

  return L.divIcon({
    html: iconHtml,
    className: "ply-3d-marker",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};

// Ply3DMarkers component
function Ply3DMarkers({ onMarkerClick, showMarkers = true }) {
  const [plyLocations, setPlyLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [tooltipPortal, setTooltipPortal] = useState(null);
  const icon = create3DIcon();

  // Load PLY locations from client data (cached by clientDataService)
  useEffect(() => {
    const clientType = getClientType();
    fetchClientJSON(clientType, 'signal_fix/ply_locations.json')
      .then(data => setPlyLocations(data))
      .catch(err => {
        console.error('Error loading PLY locations:', err);
        setPlyLocations([]);
      });
  }, []);

  // Create portal container on mount
  React.useEffect(() => {
    const portalDiv = document.createElement("div");
    portalDiv.id = "ply-tooltip-portal";
    document.body.appendChild(portalDiv);
    setTooltipPortal(portalDiv);

    return () => {
      document.body.removeChild(portalDiv);
    };
  }, []);

  // Close popup when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectedLocation &&
        !event.target.closest(".ply-3d-tooltip") &&
        !event.target.closest(".ply-3d-marker")
      ) {
        setSelectedLocation(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [selectedLocation]);

  if (!showMarkers) {
    return null;
  }

  const handleMarkerClick = (location, event) => {
    if (event && event.containerPoint) {
      // Get the map container position
      const mapContainer = event.target._map.getContainer();
      const mapRect = mapContainer.getBoundingClientRect();

      // Calculate absolute position
      setPopupPosition({
        x: mapRect.left + event.containerPoint.x,
        y: mapRect.top + event.containerPoint.y,
      });
      setSelectedLocation(location);
    }
  };

  return (
    <>
      {plyLocations.map((location) => (
        <Marker
          key={location.id}
          position={[location.lat, location.lng]}
          icon={icon}
          eventHandlers={{
            click: (e) => {
              e.originalEvent.stopPropagation();
              handleMarkerClick(location, e);
            },
          }}
        ></Marker>
      ))}

      {/* Render popup in portal */}
      {tooltipPortal &&
        selectedLocation &&
        ReactDOM.createPortal(
          <Ply3DTooltip
            location={selectedLocation}
            onView3D={(loc) => {
              setSelectedLocation(null);
              onMarkerClick(loc);
            }}
            onClose={() => setSelectedLocation(null)}
            visible={true}
            position={popupPosition}
          />,
          tooltipPortal
        )}
    </>
  );
}

export default Ply3DMarkers;
